import { AxiosError } from 'axios';
import { SignInCredentials, ValidateCredentials, RegisterCredentials } from '../../models';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchUser, registerUser, signInRequest, validateUser } from '../../services/auth.service';
import { updateUserStatus } from '../../services/profile.service';
import { User } from '../../types/user';
import { clearStorage } from '../../utils/storage';

import { api } from '../../services/api';
import { setAuthorizationHeader } from '../../services/interceptors';
import { createTokenCookies, getToken, removeTokenCookies } from '../../utils/tokenCookies';
import GeneralItens from 'services/general.service';
import { useNavigate } from 'react-router';
import { ControlDate } from 'types/controle-datas';
import { fetchControlDatesPublic } from '../../services/controle-datas.service';
import { clearAllCache } from '../../services/cache';
import { registrarLogin } from 'services/iniciativa-action.service';
import { MetodoLogin } from 'types/iniciativa-action';
import { Tutorial } from '../../types/tutorial';

interface AuthContextData {
    /**
     * `metodo` identifica o login no action log `login`. Sem ele (login por
     * credenciais na URL, usado em simulação) o front não registra: o módulo
     * de autenticação do Funifier registra `login_simulacao`.
     */
    login: (credentials: SignInCredentials, metodo?: MetodoLogin) => Promise<void | AxiosError>;
    loginWithToken: (access_token: string) => Promise<void>;
    signOut: (pathname?: string) => void;
    refreshUser: () => void;
    onValidateUser: (credentials: ValidateCredentials) => Promise<void | AxiosError>;
    onRegisterUser: (credentials: RegisterCredentials) => Promise<void | AxiosError>;
    user?: User;
    isAuthenticated: boolean;
    loadingUserData: boolean;
    controlDates?: ControlDate[];
    setControlDates: (val?: ControlDate[]) => void;
    loadControlDates: () => Promise<void>;
    tutorial?: Tutorial;
    setTutorial: (val?: Tutorial) => void;
}

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: AuthProviderProps) {
    const navigate = useNavigate();
    const token = getToken();
    const isAuthenticated = Boolean(token);

    const [user, setUser] = useState<User | undefined>();
    const [loadingUserData, setLoadingUserData] = useState(true);
    const [controlDates, setControlDates] = useState<ControlDate[]>([]);
    const [tutorial, setTutorial] = useState<Tutorial | undefined>();

    const controlDatesRef = useRef<ControlDate[]>([]);
    useEffect(() => { controlDatesRef.current = controlDates; }, [controlDates]);

    const loadControlDates = useCallback(async () => {
        try {
            const dates = await fetchControlDatesPublic();
            setControlDates(dates);
        } catch (error) {
            console.error('Erro ao carregar datas de controle:', error);
            setControlDates([]);
        }
    }, []);

    const handleSetControlDates = useCallback((val?: ControlDate[]) => {
        setControlDates(val || []);
    }, []);

    // Carrega datas de controle automaticamente ao montar (antes do login)
    useEffect(() => {
        loadControlDates();
    }, [loadControlDates]);

    const signOut = useCallback((pathname?: string) => {
        const target = pathname || '/login';
        removeTokenCookies();
        clearStorage();
        clearAllCache();
        setAuthorizationHeader(api.defaults, '');
        setUser(undefined);
        setLoadingUserData(false);
        navigate(target);
    }, [navigate]);

    const fetchUserDetail = useCallback(async () => {
        if (!user || !user._id) return;

        const token = getToken();
        if (!token) {
            console.error('Token não encontrado ao buscar detalhes do usuário');
            return;
        }

        try {
            setAuthorizationHeader(api.defaults, token);
            const { controlDates: newControlDates } = await GeneralItens(controlDatesRef.current);

            setControlDates(newControlDates);
        } catch (error) {
            console.error('Erro ao buscar detalhes do usuário:', error);
        }
    }, [user]);

    const login = useCallback(async ({ username, password }: SignInCredentials, metodo?: MetodoLogin) => {
        try {
            const auth = await signInRequest({ username, password });

            if (auth.message) {
                throw auth;
            }

            const { access_token, refreshToken, permissions, roles } = auth;

            createTokenCookies(access_token, refreshToken);
            setAuthorizationHeader(api.defaults, access_token);

            // Aguardar um pouco para garantir que o cookie foi salvo
            await new Promise(resolve => setTimeout(resolve, 100));

            const data = await fetchUser();
            const userData = { _id: data._id, ...data, permissions, roles };

            if (metodo) {
                try {
                    await registrarLogin(userData._id, metodo);
                } catch (error) {
                    console.warn('Falha ao registrar action_log de login:', error);
                }
            }

            setUser(userData);
            setLoadingUserData(false);

            return userData;
        } catch (error) {
            const err = error as AxiosError;
            console.error('Login error:', err);
            throw err;
        }
    }, []);

    const loginWithToken = useCallback(async (access_token: string) => {
        try {
            if (!access_token) {
                return;
            }

            createTokenCookies(access_token, '');
            setAuthorizationHeader(api.defaults, access_token);

            const data = await fetchUser();

            const { name, image, extra } = data;

            const userData = {
                _id: data._id,
                ...data,
                extra,
                name,
                image,
                permissions: null,
                roles: null
            };

            try {
                await registrarLogin(userData._id, 'token_url');
            } catch (error) {
                console.warn('Falha ao registrar action_log de login via token:', error);
            }

            setUser(userData);
            setLoadingUserData(false);

            return userData;
        } catch (error) {
            const err = error as AxiosError;
            throw err;
        }
    }, []);

    async function refreshUser() {
        const updatedUser = await updateUserStatus();
        setUser(updatedUser);
    }

    async function onValidateUser(data: ValidateCredentials) {
        const result = await validateUser(data);
        return result;
    }

    async function onRegisterUser(data: RegisterCredentials) {
        const result = await registerUser(data);
        if (result.status === 'OK') {
            setTimeout(async () => {
                await login({ username: result._id, password: data.password }, 'primeiro_acesso');
            }, 1e3);
        } else {
            throw result;
        }
    }

    useEffect(() => {
        if (!user || !user._id) return;
        
        const token = getToken();
        if (!token) {
            console.warn('User exists but token not found');
            return;
        }
        
        const timeout = setTimeout(() => {
            fetchUserDetail();
        }, 100);
        
        return () => clearTimeout(timeout);
    }, [user, fetchUserDetail]);

    useEffect(() => {
        const token = getToken();

        async function getUserData() {
            setLoadingUserData(true);

            try {
                const resp = await fetchUser();
                if (resp) {
                    const { _id, name, image, email, permissions, roles, detail } = resp;
                    setUser({ ...resp, _id, name, image, email, permissions, roles, detail });
                }
            } catch (error) {
                signOut();
            } finally {
                setLoadingUserData(false);
            }
        }

        if (!user && token) {
            setAuthorizationHeader(api.defaults, token);
            getUserData();
        }

        function handleSignoutEvent() {
            signOut();
        }
        
        // Proteção contra erro de message channel
        try {
            document.addEventListener('signout', handleSignoutEvent, false);
        } catch (error) {
            console.warn('Erro ao adicionar event listener:', error);
        }
        
        return () => {
            try {
                document.removeEventListener('signout', handleSignoutEvent);
            } catch (error) {
                console.warn('Erro ao remover event listener:', error);
            }
        };
    }, [user, signOut]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                loadingUserData,
                login,
                loginWithToken,
                signOut,
                refreshUser,
                onValidateUser,
                onRegisterUser,
                controlDates,
                setControlDates: handleSetControlDates,
                loadControlDates,
                tutorial,
                setTutorial,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export { AuthProvider, useAuth };
